import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import FadeIn from './shared/FadeIn';
import { projects, PROJECT_CARDS } from '../data/projects';

const statusStyles = {
  Active: { dot: 'bg-green', label: 'Live' },
  Live: { dot: 'bg-green', label: 'Live' },
  'In Development': { dot: 'bg-gold', label: 'WIP' },
  Complete: { dot: 'bg-blue', label: 'Complete' },
  Completed: { dot: 'bg-blue', label: 'Complete' },
  Failed: { dot: 'bg-red', label: 'Failed' },
  Sold: { dot: 'bg-gold', label: 'Sold' },
  'Backend Implemented': { dot: 'bg-blue', label: 'Backend' },
};

const outcomeStyles = {
  success: { bg: 'bg-green/10', border: 'border-green/30', text: 'text-green', label: 'Successful' },
  failed:  { bg: 'bg-red/10', border: 'border-red/30', text: 'text-red', label: 'Failed' },
};

const ProjectCard = React.memo(({ project, shortDesc, outcome, index, onClick }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const status = project.timeline?.status;
  const style = statusStyles[status] || statusStyles.Complete;
  const techs = (project.technologies || []).slice(0, 5);
  const screenshot = project.screenshots?.[0];

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${project.title}`}
      className="group cursor-pointer border border-border rounded-xl bg-raised/60 hover:border-green/30 hover:bg-raised hover:shadow-xl hover:shadow-green/[0.03] focus-visible:border-green/50 transition-all duration-300 flex flex-col overflow-hidden"
    >
      {/* Screenshot preview */}
      {screenshot && (
        <div className="relative h-44 overflow-hidden bg-subtle/30">
          <img
            src={screenshot.url}
            alt={screenshot.alt}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover brightness-[0.6] group-hover:brightness-[0.8] transition-all duration-500 scale-[1.02] group-hover:scale-100"
            onError={(e) => { e.target.style.visibility = 'hidden'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-raised/90 to-transparent" />
          {outcome && outcomeStyles[outcome] && (
            <span className={`absolute top-3 left-3 text-[11px] font-mono font-medium px-2.5 py-1 rounded-full border ${outcomeStyles[outcome].bg} ${outcomeStyles[outcome].border} ${outcomeStyles[outcome].text}`}>
              {outcomeStyles[outcome].label}
            </span>
          )}
        </div>
      )}
      {/* Outcome badge when no screenshot */}
      {!screenshot && outcome && outcomeStyles[outcome] && (
        <div className="px-6 pt-4">
          <span className={`text-[11px] font-mono font-medium px-2.5 py-1 rounded-full border ${outcomeStyles[outcome].bg} ${outcomeStyles[outcome].border} ${outcomeStyles[outcome].text}`}>
            {outcomeStyles[outcome].label}
          </span>
        </div>
      )}

      <div className="p-6 flex flex-col flex-1">
        {/* Title row */}
        <div className="flex items-start justify-between mb-3">
          <h3 className="min-w-0 text-lg font-display font-semibold text-txt group-hover:text-green transition-colors duration-200">
            {project.title}
          </h3>
          <div className="flex items-center gap-2 shrink-0 ml-2">
            <span className={`w-2 h-2 rounded-full ${style.dot}`} />
            <span className="text-xs text-txt-tertiary font-mono">{style.label}</span>
          </div>
        </div>

        <p className="text-sm text-txt-secondary mb-4 leading-relaxed flex-1 line-clamp-3">{shortDesc}</p>

        {project.overview?.role && (
          <p className="text-xs text-green/60 font-medium mb-3">{project.overview.role}</p>
        )}

        {/* Tech tags */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          {techs.map((t) => (
            <span
              key={t}
              className="text-xs font-mono px-2 py-0.5 bg-surface/80 text-txt-tertiary rounded border border-border/60"
            >
              {t}
            </span>
          ))}
          {project.technologies?.length > 5 && (
            <span className="text-xs px-2 py-0.5 text-txt-tertiary">
              +{project.technologies.length - 5}
            </span>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-1 text-xs pt-3 border-t border-border/40">
          {project.links?.github && (
            <a
              href={project.links.github}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              className="text-txt-secondary hover:text-txt transition-colors py-2.5 px-3 -my-2.5 -ml-3 rounded-md hover:bg-surface/50"
            >
              GitHub<span className="sr-only"> (opens in new tab)</span>
            </a>
          )}
          {project.links?.demo && (
            <a
              href={project.links.demo}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              className="text-txt-secondary hover:text-txt transition-colors py-2.5 px-3 -my-2.5 rounded-md hover:bg-surface/50"
            >
              Live<span className="sr-only"> (opens in new tab)</span>
            </a>
          )}
          <span className="ml-auto text-green font-medium flex items-center gap-1 group-hover:gap-2 transition-all duration-200" aria-hidden="true">
            Details
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </motion.article>
  );
});

const Projects = () => {
  const navigate = useNavigate();
  const cardData = PROJECT_CARDS.map((c) => ({
    ...c,
    project: projects.find((p) => p.id === c.id),
  })).filter((c) => c.project);

  return (
    <FadeIn id="projects" className="py-28 content-auto">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <span className="text-xs font-mono text-green/50 tracking-widest mb-3 block" aria-hidden="true">03</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">Projects</h2>
          <p className="text-txt-secondary text-lg">
            Products I've built, co-founded, or led the backend for
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cardData.map(({ id, short, outcome, project }, i) => (
            <ProjectCard
              key={id}
              project={project}
              shortDesc={short}
              outcome={outcome}
              index={i}
              onClick={() => navigate(`/projects/${id}`)}
            />
          ))}
        </div>
      </div>
    </FadeIn>
  );
};

export default Projects;
